import { useEffect, useRef, useState } from 'react';
import getProfileApi from 'api/profile/getProfile.api';
import { CACHE_KEYS, readCache, writeCache } from 'newApp/helpers/requestCache';
import type { AccountOverview } from 'newApp/types/account';

export interface AccountOverviewOwner {
  opensooq_member_id: number;
  profile_picture: string;
  company_mobile: string;
  account_owner: string;
}

export interface AccountOverviewWallet {
  balance_label?: string;
  bundle_quantity_label?: string;
  bundle_summary_label?: Record<string, string>;
}

export interface AccountOverviewPayload extends AccountOverview {
  member_owner?: AccountOverviewOwner | null;
  wallet?: AccountOverviewWallet | null;
}

let inFlight: Promise<AccountOverviewPayload> | null = null;

export const fetchAccountOverview = (): Promise<AccountOverviewPayload> => {
  const cached = readCache<AccountOverviewPayload>(CACHE_KEYS.accountOverview);

  if (cached) {
    return Promise.resolve(cached);
  }

  if (!inFlight) {
    inFlight = getProfileApi()
      .then((response) => {
        const payload = response.data as AccountOverviewPayload;
        writeCache(CACHE_KEYS.accountOverview, payload);
        inFlight = null;

        return payload;
      })
      .catch((error: unknown) => {
        inFlight = null;

        throw error;
      });
  }

  return inFlight;
};

const useAccountOverview = () => {
  const cached = readCache<AccountOverviewPayload>(CACHE_KEYS.accountOverview);
  const [data, setData] = useState<AccountOverviewPayload | undefined>(cached);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(!cached);
  const isCurrent = useRef(true);

  useEffect(() => {
    isCurrent.current = true;

    fetchAccountOverview()
      .then((payload) => {
        if (!isCurrent.current) return;
        setData(payload);
        setError(null);
        setIsLoading(false);
      })
      .catch(() => {
        if (!isCurrent.current) return;
        setError('Unexpected request error');
        setIsLoading(false);
      });

    return () => {
      isCurrent.current = false;
    };
  }, []);

  return { data, error, isLoading };
};

export default useAccountOverview;
